import React, { useState, useEffect } from 'react';
import './AddProductForm.css';
import ProductFormByType from './forms/ProductFormByType';
import { useAuth } from '../context/AuthContext';

// Hilfsfunktion um Domain aus URL zu extrahieren
const extractDomain = (url) => {
  if (!url) return 'Shop';
  try {
    const urlObj = new URL(url);
    // Entferne "www." wenn vorhanden
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return 'Shop';
  }
};

function AddProductForm({ onProductAdded }) {
  const { fetchWithAuth } = useAuth();
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [productTypes, setProductTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [formData, setFormData] = useState({
    // Basis-Felder
    name: '',
    description: '',
    productTypeId: '',
    manufacturerId: '',
    image: '',
    datasheet: '',
    phases: [],
    
    // Beleuchtungs-Felder
    watts: '',
    umolPerSecond: '',
    efficiency: '',
    recommendedArea: '',
    growTentSize: '',
    lifespan: '',
    lifespanType: 'L80',
    smartControl: '',
    dimmable: '',
    dimmerIncluded: false,
    
    // Luft & Klima-Felder
    airFlowRate: '',
    noiseLevel: '',
    powerConsumption: '',
    temperatureControl: '',
    humidityControl: '',
    connections: '',
    tankVolume: '',
    climateSmartControl: false,
    climateDescription: '',
    
    // Messtechnik-Felder
    measurementType: '',
    measurementRange: '',
    accuracy: '',
    connectivity: '',
    displayType: '',
    batteryType: '',
    measurementFeatures: '',
    
    // Growzelt-Felder
    tentSize: '',
    tentHeight: '',
    tentArea: '',
    material: '',
    reflectivity: '',
    thickness: '',
    framework: '',
    hasFloor: false,
    tentFeatures: '',

    // Dünger-Felder
    fertilizerProductLine: '',
    fertilizerType: '',
    fertilizerFormulation: '',
    fertilizerPackageSizes: [],
    fertilizerNpk: '',
    fertilizerNutrients: [],
    fertilizerHumicAcids: false,
    fertilizerFulvicAcids: false,
    fertilizerPhaseSuitability: [],
    fertilizerSystemCompatibility: [],
    fertilizerAutoIrrigationSuitable: false,
    fertilizerPhRange: '',
    fertilizerEcStandardDose: '',
    fertilizerDosage: '',
    fertilizerApplicationFrequency: '',
    fertilizerFoliarPossible: false,
    fertilizerComboProducts: '',
    fertilizerNutrientScheduleLink: '',
    fertilizerPricePerBottle: '',
    fertilizerPricePerLiter: '',
    fertilizerSolutionPerEuro: ''
  });

  const [shopConfigs, setShopConfigs] = useState([]);
  const [testingSelectors, setTestingSelectors] = useState({});
  const [testResults, setTestResults] = useState({});
  const [priceSelectionModal, setPriceSelectionModal] = useState(null);
  const [selectedPriceIndex, setSelectedPriceIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Lade Shops, Produkttypen und Hersteller von der API beim Mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shopsRes, typesRes, manufacturersRes] = await Promise.all([
          fetch('/api/stores'),
          fetch('/api/product-types'),
          fetch('/api/manufacturers')
        ]);
        
        const shopsData = await shopsRes.json();
        const typesData = await typesRes.json();
        const manufacturersData = await manufacturersRes.json();
        
        setShops(Array.isArray(shopsData) ? shopsData : []);
        setProductTypes(Array.isArray(typesData) ? typesData : []);
        setManufacturers(Array.isArray(manufacturersData) ? manufacturersData : []);
        
        // Initialisiere shopConfigs mit echten MongoDB IDs
        const configs = (Array.isArray(shopsData) ? shopsData : []).map(shop => ({
          storeId: shop._id,
          shopName: shop.name,
          productUrl: '',
          storeWebsite: shop.website || '',
          priceSelector: '',
          titleSelector: '',
          imageSelector: '',
          status: 'inactive'
        }));
        setShopConfigs(configs);
        setShopsLoading(false);
      } catch (err) {
        console.error('❌ Fehler beim Laden der Daten:', err);
        setError('Fehler beim Laden der Daten');
        setShopsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Mapping von Zeltgröße zu Fläche (m²)
  // Mapping von Zeltgröße zu Fläche (m²)
  const growTentSizeToArea = {
    '40x40': '0.16',
    '40x60': '0.24',
    '40x80': '0.32',
    '40x100': '0.40',
    '60x60': '0.36',
    '60x100': '0.60',
    '60x120': '0.72',
    '60x150': '0.90',
    '80x80': '0.64',
    '80x100': '0.80',
    '80x120': '0.96',
    '80x150': '1.20',
    '80x200': '1.60',
    '100x100': '1.00',
    '100x150': '1.50',
    '100x200': '2.00',
    '120x120': '1.44',
    '120x150': '1.80',
    '120x180': '2.16',
    '120x200': '2.40',
    '120x240': '2.88',
    '150x150': '2.25',
    '150x200': '3.00',
    '150x240': '3.60',
    '200x200': '4.00',
    '240x240': '5.76'
  };

  // Reverse Mapping von Fläche zu Zeltgröße
  const areaToGrowTentSize = Object.entries(growTentSizeToArea).reduce((acc, [size, area]) => {
    acc[area] = size;
    return acc;
  }, {});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    // Wenn Growzelt-Größe geändert wird, automatisch die Fläche setzen (nur bei Beleuchtung)
    if (name === 'growTentSize' && growTentSizeToArea[value]) {
      newFormData.recommendedArea = growTentSizeToArea[value];
    }

    // Wenn Empfohlene Fläche geändert wird, automatisch die Zeltgröße setzen (nur bei Beleuchtung)
    if (name === 'recommendedArea' && areaToGrowTentSize[value]) {
      newFormData.growTentSize = areaToGrowTentSize[value];
    }

    setFormData(newFormData);
  };

  const handlePhaseChange = (phase) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.includes(phase)
        ? prev.phases.filter(p => p !== phase)
        : [...prev.phases, phase]
    }));
  };

  const handleArrayChange = (fieldName, value) => {
    setFormData(prev => {
      const current = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];

      return {
        ...prev,
        [fieldName]: updated
      };
    });
  };

  const handleShopChange = (storeId, field, value) => {
    setShopConfigs(prev =>
      prev.map(shop => {
        if (shop.storeId === storeId) {
          const updated = { ...shop, [field]: value };

          // Auto-validiere Status
          if (field === 'priceSelector' || field === 'productUrl') {
            if (!updated.productUrl || !updated.priceSelector) {
              updated.status = 'inactive';
            } else {
              // Validiere CSS-Selektor
              try {
                // Einfache Validierung: kein leerer String, enthält gültige CSS-Zeichen
                const isValidSelector = updated.priceSelector.trim().length > 0 && 
                  /^[.#[-_a-zA-Z0-9\s:,>~+]+$/.test(updated.priceSelector);
                updated.status = isValidSelector ? 'valid' : 'error';
              } catch {
                updated.status = 'error';
              }
            }
          }
          return updated;
        }
        return shop;
      })
    );
  };

  const handleTestSelector = async (storeId) => {
    const shop = shopConfigs.find(s => s.storeId === storeId);
    if (!shop || !shop.productUrl) {
      alert('Bitte geben Sie die Produkt-URL ein');
      return;
    }

    setTestingSelectors(prev => ({ ...prev, [storeId]: true }));
    setTestResults(prev => ({ ...prev, [storeId]: null }));

    try {
      // Rufe Auto-Find Endpoint auf
      const res = await fetchWithAuth('/api/products/find-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productUrl: shop.productUrl
        })
      });

      const data = await res.json();
      
      if (data.found) {
        // Automatisch den höchsten Preis nehmen
        let selectedPrice = data.selectedPrice;
        
        if (!selectedPrice && data.prices && data.prices.length > 0) {
          // Sortiere absteigend und nimm den höchsten
          const sortedPrices = [...data.prices].sort((a, b) => {
            const priceA = parseFloat(a.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            const priceB = parseFloat(b.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            return priceB - priceA;
          });
          selectedPrice = sortedPrices[0];
        }
        
        if (selectedPrice) {
          setShopConfigs(prev =>
            prev.map(s =>
              s.storeId === storeId 
                ? { 
                    ...s, 
                    priceSelector: selectedPrice.priceSelector,
                    status: 'valid'
                  }
                : s
            )
          );
          setTestResults(prev => ({ ...prev, [storeId]: { found: true, price: selectedPrice.price, priceText: selectedPrice.priceText } }));
          
          // Preis-Speicherung erfolgt beim Speichern des Produkts
        }
      }
    } catch (err) {
      console.error('Test error:', err);
      setTestResults(prev => ({ 
        ...prev, 
        [storeId]: { 
          found: false, 
          message: 'Fehler: ' + err.message 
        } 
      }));
    } finally {
      setTestingSelectors(prev => ({ ...prev, [storeId]: false }));
    }
  };

  const handleConfirmPrice = () => {
    if (!priceSelectionModal) return;

    const selectedPrice = priceSelectionModal.prices[selectedPriceIndex];
    const storeId = priceSelectionModal.storeId;

    // Update shop config mit ausgewähltem Preis
    setShopConfigs(prev =>
      prev.map(s =>
        s.storeId === storeId 
          ? { 
              ...s, 
              priceSelector: selectedPrice.priceSelector,
              status: 'valid'
            }
          : s
      )
    );

    setTestResults(prev => ({ 
      ...prev, 
      [storeId]: { found: true, price: selectedPrice.price, priceText: selectedPrice.priceText } 
    }));

    setPriceSelectionModal(null);
  };

  const handleAutoSearchAllPrices = async (currentShopConfigs) => {
    const shopsToSearch = currentShopConfigs.filter(s => s.productUrl && !s.priceSelector);
    
    if (shopsToSearch.length === 0) {
      return currentShopConfigs;
    }

    let updatedConfigs = [...currentShopConfigs];
    const newTestResults = {};

    // Suche Preise für alle Shops sequenziell
    for (const shop of shopsToSearch) {
      setTestingSelectors(prev => ({ ...prev, [shop.storeId]: true }));

      try {
        const res = await fetchWithAuth('/api/products/find-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productUrl: shop.productUrl
          })
        });

        const data = await res.json();
        
        if (data.found) {
          let selectedPrice = data.selectedPrice;
          
          if (!selectedPrice && data.prices && data.prices.length > 0) {
            // Nimm den höchsten Preis
            const sortedPrices = [...data.prices].sort((a, b) => {
              const priceA = parseFloat(a.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
              const priceB = parseFloat(b.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
              return priceB - priceA;
            });
            selectedPrice = sortedPrices[0];
          }
          
          if (selectedPrice) {
            // Update die lokale updatedConfigs Kopie
            updatedConfigs = updatedConfigs.map(s =>
              s.storeId === shop.storeId 
                ? { 
                    ...s, 
                    priceSelector: selectedPrice.priceSelector,
                    status: 'valid'
                  }
                : s
            );
            newTestResults[shop.storeId] = { found: true, price: selectedPrice.price, priceText: selectedPrice.priceText };
          }
        } else {
          newTestResults[shop.storeId] = { found: false, message: 'Kein Preis gefunden' };
        }
      } catch (err) {
        newTestResults[shop.storeId] = { found: false, message: 'Fehler: ' + err.message };
      } finally {
        setTestingSelectors(prev => ({ ...prev, [shop.storeId]: false }));
      }

      // Kleine Pause zwischen Requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Update die UI mit allen neuen Ergebnissen
    setTestResults(prev => ({ ...prev, ...newTestResults }));
    setShopConfigs(updatedConfigs);

    return updatedConfigs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Produktname ist erforderlich');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      // SCHRITT 1: Automatisch alle Preise suchen
      const updatedConfigs = await handleAutoSearchAllPrices(shopConfigs);

      // SCHRITT 2: Mit aktualisierten shopConfigs weitermachen
      
      // Nach der Preissuche filtern mit aktualisierten Configs
      const shopsWithConfig = updatedConfigs.filter(s => s.priceSelector && s.productUrl);

      if (shopsWithConfig.length === 0) {
        setError('Bitte konfigurieren Sie mindestens einen Shop mit Produkt-URL oder aktivieren Sie die Preissuche');
        setLoading(false);
        return;
      }

      // Erstelle Produkt mit Shop-Konfigurationen

      // Helper: Finde den Produkttyp-Namen
      const getProductTypeName = () => {
        const type = productTypes.find(t => t._id === formData.productTypeId);
        return type?.name;
      };

      const typeName = getProductTypeName();

      // Erstelle die Basis-Produktdaten
      const productData = {
        name: formData.name,
        description: formData.description,
        image: formData.image,
        datasheet: formData.datasheet || undefined,
        productTypeId: formData.productTypeId || undefined,
        manufacturerId: formData.manufacturerId || undefined,
        categories: formData.phases.map(phase => ({
          type: 'Phase',
          name: phase
        })),
        shops: shopsWithConfig.map(s => ({
          storeId: s.storeId,
          shopName: s.shopName,
          productUrl: s.productUrl,
          priceSelector: s.priceSelector,
          titleSelector: s.titleSelector || '',
          imageSelector: s.imageSelector || ''
        }))
      };

      // Ergänze produkttyp-spezifische Felder
      if (typeName === 'Beleuchtung') {
        productData.lightingSpecs = {
          watts: formData.watts ? parseFloat(formData.watts) : undefined,
          umolPerSecond: formData.umolPerSecond ? parseFloat(formData.umolPerSecond) : undefined,
          efficiency: formData.efficiency ? parseFloat(formData.efficiency) : undefined,
          recommendedArea: formData.recommendedArea ? parseFloat(formData.recommendedArea) : undefined,
          growTentSize: formData.growTentSize || undefined,
          lifespan: formData.lifespan ? parseFloat(formData.lifespan) : undefined,
          lifespanType: formData.lifespanType || 'L80',
          smartControl: formData.smartControl || undefined,
          dimmable: formData.dimmable || undefined,
          dimmerIncluded: formData.dimmerIncluded || false
        };
        
        // Legacy-Kompatibilität (für alte Abfragen)
        productData.watts = formData.watts ? parseFloat(formData.watts) : undefined;
        productData.umolPerSecond = formData.umolPerSecond ? parseFloat(formData.umolPerSecond) : undefined;
        productData.efficiency = formData.efficiency ? parseFloat(formData.efficiency) : undefined;
        productData.recommendedArea = formData.recommendedArea ? parseFloat(formData.recommendedArea) : undefined;
        productData.growTentSize = formData.growTentSize || undefined;
        productData.lifespan = formData.lifespan ? parseFloat(formData.lifespan) : undefined;
        productData.lifespanType = formData.lifespanType || 'L80';
        productData.smartControl = formData.smartControl || undefined;
        productData.dimmable = formData.dimmable || undefined;
        productData.dimmerIncluded = formData.dimmerIncluded || false;
      } else if (typeName === 'Luft & Klima') {
        productData.climateSpecs = {
          airFlowRate: formData.airFlowRate ? parseFloat(formData.airFlowRate) : undefined,
          noiseLevel: formData.noiseLevel ? parseFloat(formData.noiseLevel) : undefined,
          powerConsumption: formData.powerConsumption ? parseFloat(formData.powerConsumption) : undefined,
          temperatureControl: formData.temperatureControl || undefined,
          humidityControl: formData.humidityControl || undefined,
          connections: formData.connections || undefined,
          tankVolume: formData.tankVolume ? parseFloat(formData.tankVolume) : undefined,
          smartControl: formData.climateSmartControl || false,
          climateDescription: formData.climateDescription || undefined
        };
      } else if (typeName === 'Messtechnik') {
        productData.measurementSpecs = {
          measurementType: formData.measurementType || undefined,
          measurementRange: formData.measurementRange || undefined,
          accuracy: formData.accuracy || undefined,
          connectivity: formData.connectivity || undefined,
          displayType: formData.displayType || undefined,
          batteryType: formData.batteryType || undefined,
          measurementFeatures: formData.measurementFeatures || undefined
        };
      } else if (typeName === 'Growzelte') {
        productData.tentSpecs = {
          tentSize: formData.tentSize || undefined,
          tentHeight: formData.tentHeight ? parseFloat(formData.tentHeight) : undefined,
          tentArea: formData.tentArea ? parseFloat(formData.tentArea) : undefined,
          material: formData.material || undefined,
          reflectivity: formData.reflectivity ? parseFloat(formData.reflectivity) : undefined,
          thickness: formData.thickness ? parseFloat(formData.thickness) : undefined,
          framework: formData.framework || undefined,
          hasFloor: formData.hasFloor || false,
          tentFeatures: formData.tentFeatures || undefined
        };
      } else if (typeName === 'Dünger') {
        const comboProducts = (formData.fertilizerComboProducts || '')
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);

        productData.fertilizerSpecs = {
          productLine: formData.fertilizerProductLine || undefined,
          fertilizerType: formData.fertilizerType || undefined,
          formulation: formData.fertilizerFormulation || undefined,
          packageSizes: formData.fertilizerPackageSizes || [],
          npk: formData.fertilizerNpk || undefined,
          nutrients: formData.fertilizerNutrients || [],
          hasHumicAcids: formData.fertilizerHumicAcids || false,
          hasFulvicAcids: formData.fertilizerFulvicAcids || false,
          phaseSuitability: formData.fertilizerPhaseSuitability || [],
          systemCompatibility: formData.fertilizerSystemCompatibility || [],
          autoIrrigationSuitable: formData.fertilizerAutoIrrigationSuitable || false,
          phRange: formData.fertilizerPhRange || undefined,
          ecStandardDose: formData.fertilizerEcStandardDose || undefined,
          dosageMlPerLiter: formData.fertilizerDosage || undefined,
          applicationFrequency: formData.fertilizerApplicationFrequency || undefined,
          foliarPossible: formData.fertilizerFoliarPossible || false,
          comboProducts: comboProducts,
          nutrientScheduleLink: formData.fertilizerNutrientScheduleLink || undefined,
          pricePerBottle: formData.fertilizerPricePerBottle ? parseFloat(formData.fertilizerPricePerBottle) : undefined,
          pricePerLiter: formData.fertilizerPricePerLiter ? parseFloat(formData.fertilizerPricePerLiter) : undefined,
          nutrientSolutionPerEuro: formData.fertilizerSolutionPerEuro ? parseFloat(formData.fertilizerSolutionPerEuro) : undefined
        };
      }

      const response = await fetchWithAuth('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        const createdProduct = await response.json();
        
        // SCHRITT 3: Speichere alle gefundenen Preise in die DB
        let pricesSaved = 0;
        for (const [storeId, testResult] of Object.entries(testResults)) {
          if (testResult?.found && testResult?.price) {
            try {
              const shopConfig = updatedConfigs.find(s => s.storeId === storeId);
              
              const priceResponse = await fetchWithAuth('/api/prices', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  productId: createdProduct._id || createdProduct.id,
                  retailer: shopConfig ? extractDomain(shopConfig.productUrl) : 'Unknown',
                  price: parseFloat(testResult.price),
                  currency: 'EUR',
                  url: shopConfig?.productUrl,
                  inStock: true,
                  productImage: formData.image
                })
              });
              
              if (priceResponse.ok) {
                pricesSaved++;
              }
            } catch (err) {
              // Fehler stillschweigend ignorieren
            }
          }
        }

        setSuccessMessage(`✅ Produkt "${formData.name}" erfolgreich erstellt! (${pricesSaved} Preise aktualisiert)`);
        setFormData({ 
          // Basis-Felder
          name: '', 
          description: '', 
          image: '', 
          datasheet: '',
          productTypeId: '',
          manufacturerId: '',
          phases: [],
          
          // Beleuchtungs-Felder
          watts: '',
          umolPerSecond: '',
          efficiency: '',
          recommendedArea: '',
          growTentSize: '',
          lifespan: '',
          lifespanType: 'L80',
          smartControl: '',
          dimmable: '',
          dimmerIncluded: false,
          
          // Luft & Klima-Felder
          airFlowRate: '',
          noiseLevel: '',
          powerConsumption: '',
          temperatureControl: '',
          humidityControl: '',
          connections: '',
          tankVolume: '',
          climateSmartControl: false,
          climateDescription: '',
          
          // Messtechnik-Felder
          measurementType: '',
          measurementRange: '',
          accuracy: '',
          connectivity: '',
          displayType: '',
          batteryType: '',
          measurementFeatures: '',
          
          // Growzelt-Felder
          tentSize: '',
          tentHeight: '',
          tentArea: '',
          material: '',
          reflectivity: '',
          thickness: '',
          framework: '',
          hasFloor: false,
          tentFeatures: '',

          // Dünger-Felder
          fertilizerProductLine: '',
          fertilizerType: '',
          fertilizerFormulation: '',
          fertilizerPackageSizes: [],
          fertilizerNpk: '',
          fertilizerNutrients: [],
          fertilizerHumicAcids: false,
          fertilizerFulvicAcids: false,
          fertilizerPhaseSuitability: [],
          fertilizerSystemCompatibility: [],
          fertilizerAutoIrrigationSuitable: false,
          fertilizerPhRange: '',
          fertilizerEcStandardDose: '',
          fertilizerDosage: '',
          fertilizerApplicationFrequency: '',
          fertilizerFoliarPossible: false,
          fertilizerComboProducts: '',
          fertilizerNutrientScheduleLink: '',
          fertilizerPricePerBottle: '',
          fertilizerPricePerLiter: '',
          fertilizerSolutionPerEuro: ''
        });
        setShopConfigs(
          shops.map(shop => ({
            storeId: shop._id,
            shopName: shop.name,
            productUrl: '',
            storeWebsite: shop.website || '',
            priceSelector: '',
            titleSelector: '',
            imageSelector: '',
            status: 'inactive'
          }))
        );
        setTestResults({});
        setTimeout(() => {
          onProductAdded();
          setSuccessMessage('');
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Fehler beim Hinzufügen des Produkts');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      setError('Fehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="add-product-form" onSubmit={handleSubmit}>
      <h2>+ Neues Produkt mit Shop-Konfiguration</h2>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {shopsLoading && (
        <div className="loading-message">
          ⏳ Shops werden geladen... Bitte warten.
        </div>
      )}

      {/* Basis-Infos */}
      <div className="product-info-section">
        <h3>📦 Produkt-Informationen</h3>

        <div className="form-group">
          <label htmlFor="name">Produktname *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="z.B. SANlight EVO 3-60"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Beschreibung</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optionale Produktbeschreibung..."
            disabled={loading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="productTypeId">Produkttyp</label>
            <select
              id="productTypeId"
              name="productTypeId"
              value={formData.productTypeId}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">-- Bitte wählen --</option>
              {productTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="manufacturerId">Hersteller</label>
            <select
              id="manufacturerId"
              name="manufacturerId"
              value={formData.manufacturerId}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">-- Bitte wählen --</option>
              {manufacturers.map(mfg => (
                <option key={mfg._id} value={mfg._id}>
                  {mfg.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="datasheet">📄 Datenblatt-URL</label>
            <input
              type="url"
              id="datasheet"
              name="datasheet"
              value={formData.datasheet}
              onChange={handleChange}
              placeholder="https://..."
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="image">🖼️ Bild-URL</label>
            <input
              type="url"
              id="image"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://..."
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Produkttyp-spezifische Felder */}
      <ProductFormByType 
        selectedProductTypeId={formData.productTypeId}
        productTypes={productTypes}
        formData={formData}
        handleChange={handleChange}
        handlePhaseChange={handlePhaseChange}
        handleArrayChange={handleArrayChange}
        disabled={loading}
      />

      {/* Shop-Konfigurationen */}
      <div className="shop-prices-section">
        <h3>🛒 Shop-Konfigurationen ({shopConfigs.filter(s => s.status === 'valid').length}/{shopConfigs.length})</h3>
        <p className="shop-help-text">
          💡 Gib die Shop-URL ein und klick 🔍 um den Preis automatisch zu finden
        </p>

        <div className="shop-configs-vertical">
          {shopConfigs.map((shop) => (
            <div key={shop.storeId} className={`shop-config-item status-${shop.status}`}>
              <div className="shop-config-header">
                <div className="shop-name-status">
                  <h4>
                    <a 
                      href={shop.productUrl || shop.storeWebsite || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: 'inherit', 
                        textDecoration: (shop.productUrl || shop.storeWebsite) ? 'underline' : 'none',
                        cursor: (shop.productUrl || shop.storeWebsite) ? 'pointer' : 'default'
                      }}
                    >
                      {shop.productUrl ? extractDomain(shop.productUrl) : extractDomain(shop.storeWebsite) || shop.shopName}
                    </a>
                  </h4>
                  <span className={`status-badge status-${shop.status}`}>
                    {shop.status === 'inactive' && '⚪'}
                    {shop.status === 'error' && '🟡'}
                    {shop.status === 'valid' && '🟢'}
                  </span>
                </div>
              </div>

              <div className="shop-config-fields">
                <div className="field-group">
                  <label htmlFor={`url-${shop.storeId}`}>🔗 Produkt-URL *</label>
                  <div className="selector-input-group">
                    <input
                      type="url"
                      id={`url-${shop.storeId}`}
                      placeholder="https://example.com/product"
                      value={shop.productUrl}
                      onChange={(e) => handleShopChange(shop.storeId, 'productUrl', e.target.value)}
                      disabled={loading}
                      className="url-input"
                    />
                    <button
                      type="button"
                      className="btn-test-selector"
                      onClick={() => handleTestSelector(shop.storeId)}
                      disabled={loading || testingSelectors[shop.storeId] || !shop.productUrl}
                      title="Preis automatisch finden"
                    >
                      {testingSelectors[shop.storeId] ? '⏳' : '🔍'}
                    </button>
                    
                    {testResults[shop.storeId] && (
                      <div className={`test-result-inline ${testResults[shop.storeId].found ? 'success' : 'error'}`}>
                        {testResults[shop.storeId].found ? (
                          <>
                            <strong>✅ Preis gefunden!</strong>
                            <code>{testResults[shop.storeId].priceText}</code>
                          </>
                        ) : (
                          <strong>❌ Nicht gefunden</strong>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" className="btn btn-primary btn-large" disabled={loading || shopsLoading || shopConfigs.length === 0}>
        {shopsLoading ? '⏳ Shops werden geladen...' : loading ? '⏳ Wird erstellt und Preise werden gesucht...' : '✨ Produkt erstellen (Preise werden automatisch gesucht)'}
      </button>

      {/* Preisauswahl-Modal */}
      {priceSelectionModal && (
        <div className="modal-overlay" onClick={() => setPriceSelectionModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>💰 Mehrere Preise gefunden - Bitte wählen</h3>
            <p>{priceSelectionModal.message}</p>
            
            <div className="price-options">
              {priceSelectionModal.prices.map((price, idx) => (
                <label key={idx} className={`price-option ${selectedPriceIndex === idx ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="price-selection"
                    value={idx}
                    checked={selectedPriceIndex === idx}
                    onChange={() => setSelectedPriceIndex(idx)}
                  />
                  <span className="price-info">
                    <strong>{price.price.toFixed(2)}€</strong>
                    <small>{price.priceText}</small>
                    <tiny className="selector-info">Selektor: {price.priceSelector}</tiny>
                  </span>
                </label>
              ))}
            </div>

            <div className="modal-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setPriceSelectionModal(null)}
              >
                ❌ Abbrechen
              </button>
              <button 
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmPrice}
              >
                ✅ Preis bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default AddProductForm;
